/* =============================================================================
   decor.jsx  —  watercolor-style garden motifs (stylized SVG).
   Ported from the prototype: globals -> ES module exports. JSX unchanged.
============================================================================= */
import { useId } from 'react';

/* shared soft-paint filter wrapper so edges feel painted, not vector-sharp */
function Paint({ children, blur = 0.6, idBase }) {
  const fid = `wc-${idBase}`;
  return (
    <g filter={`url(#${fid})`}>
      <defs>
        <filter id={fid} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation={blur} result="b" />
          <feComponentTransfer in="b">
            <feFuncA type="linear" slope="1.05" />
          </feComponentTransfer>
        </filter>
      </defs>
      {children}
    </g>
  );
}

/* ------------------------------- Leaf sprig ------------------------------- */
export function LeafSprig({ size = 120, flip = false, style, className = '' }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      className={className}
      style={{ transform: flip ? 'scaleX(-1)' : 'none', ...style }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`lf${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#92a36a" />
          <stop offset="1" stopColor="#4f6138" />
        </linearGradient>
      </defs>
      <Paint idBase={`leaf${id}`} blur={0.5}>
        <path d="M62 116 C58 86 56 56 60 18" fill="none" stroke="#5e6f44" strokeWidth="2.4" strokeLinecap="round" />
        {[
          { x: 60, y: 26, r: -38, s: 1 },
          { x: 60, y: 50, r: 40, s: 1.05 },
          { x: 60, y: 74, r: -34, s: 0.95 },
          { x: 60, y: 96, r: 36, s: 0.85 },
        ].map((l, i) => (
          <g key={i} transform={`translate(${l.x} ${l.y}) rotate(${l.r}) scale(${l.s})`}>
            <path
              d="M0 0 C20 -6 40 4 50 22 C30 22 8 16 0 0 Z"
              fill={`url(#lf${id})`}
              opacity="0.92"
            />
            <path d="M2 2 C18 6 34 12 47 21" fill="none" stroke="#3f5230" strokeWidth="1.1" opacity="0.5" />
          </g>
        ))}
      </Paint>
    </svg>
  );
}

/* -------------------------------- Ladybug -------------------------------- */
export function Ladybug({ size = 48, style, className = '' }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" className={className} style={style} aria-hidden="true">
      <Paint idBase={`lb${id}`} blur={0.45}>
        <ellipse cx="30" cy="22" rx="9" ry="8" fill="#2b2622" />
        <circle cx="26" cy="20" r="1.6" fill="#fff" opacity="0.85" />
        <circle cx="34" cy="20" r="1.6" fill="#fff" opacity="0.85" />
        <path d="M30 28 C14 28 13 56 30 56 C47 56 46 28 30 28 Z" fill="#c0392b" />
        <path d="M30 28 C24 28 20 40 21 54" fill="none" stroke="#7d1c14" strokeWidth="1" opacity="0.3" />
        <line x1="30" y1="29" x2="30" y2="55" stroke="#2b2622" strokeWidth="2" />
        {[[23,38],[37,38],[22,48],[38,48],[30,50]].map(([x,y],i)=>(
          <circle key={i} cx={x} cy={y} r="2.4" fill="#2b2622" />
        ))}
      </Paint>
    </svg>
  );
}

/* ---------------------------------- Bee ---------------------------------- */
export function Bee({ size = 52, style, className = '' }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg width={size} height={size} viewBox="0 0 70 60" className={className} style={style} aria-hidden="true">
      <Paint idBase={`bee${id}`} blur={0.5}>
        {/* wings */}
        <ellipse cx="30" cy="20" rx="13" ry="9" fill="#dfeaf0" opacity="0.7" transform="rotate(-20 30 20)" />
        <ellipse cx="42" cy="20" rx="11" ry="8" fill="#dfeaf0" opacity="0.6" transform="rotate(20 42 20)" />
        {/* body */}
        <ellipse cx="38" cy="36" rx="18" ry="12" fill="#e8b84b" />
        <path d="M30 26 C28 34 30 44 35 47" stroke="#2b2622" strokeWidth="4" fill="none" opacity="0.92" />
        <path d="M41 26 C44 33 45 43 42 47" stroke="#2b2622" strokeWidth="4" fill="none" opacity="0.92" />
        <ellipse cx="55" cy="34" rx="6" ry="6" fill="#2b2622" />
        <circle cx="57" cy="32" r="1.4" fill="#fff" opacity="0.8" />
      </Paint>
    </svg>
  );
}

/* ------------------------------- Dragonfly ------------------------------- */
export function Dragonfly({ size = 130, flip = false, style, className = '' }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg
      width={size}
      height={size * 0.72}
      viewBox="0 0 180 130"
      className={className}
      style={{ transform: flip ? 'scaleX(-1)' : 'none', ...style }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`dfw${id}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#3e7c8c" stopOpacity="0.42" />
          <stop offset="1" stopColor="#bcd6db" stopOpacity="0.18" />
        </linearGradient>
      </defs>
      <Paint idBase={`df${id}`} blur={0.5}>
        {/* four long wings */}
        <ellipse cx="120" cy="48" rx="56" ry="13" fill={`url(#dfw${id})`} transform="rotate(-14 120 48)" />
        <ellipse cx="120" cy="78" rx="52" ry="12" fill={`url(#dfw${id})`} transform="rotate(12 120 78)" />
        <ellipse cx="64" cy="50" rx="40" ry="10" fill={`url(#dfw${id})`} transform="rotate(10 64 50)" />
        <ellipse cx="66" cy="76" rx="38" ry="9" fill={`url(#dfw${id})`} transform="rotate(-10 66 76)" />
        {/* body */}
        <circle cx="58" cy="63" r="9" fill="#46663f" />
        <circle cx="55" cy="60" r="1.8" fill="#fff" opacity="0.7" />
        <path d="M64 63 C92 62 128 62 168 65" stroke="#5b7a4f" strokeWidth="6" strokeLinecap="round" fill="none" />
        {[88,104,120,136,150].map((x,i)=>(
          <circle key={i} cx={x} cy={63 + (i*0.5)} r="3.6" fill="#e0a82b" opacity="0.9" />
        ))}
      </Paint>
    </svg>
  );
}

/* ------------------------- Butterfly (monarch, stylized) ----------------- */
export function Butterfly({ size = 110, flip = false, style, className = '' }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      className={className}
      style={{ transform: flip ? 'scaleX(-1)' : 'none', ...style }}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={`bw${id}`} cx="0.5" cy="0.4" r="0.7">
          <stop offset="0" stopColor="#f0a23e" />
          <stop offset="0.7" stopColor="#e08a2b" />
          <stop offset="1" stopColor="#c66f1d" />
        </radialGradient>
      </defs>
      <Paint idBase={`bf${id}`} blur={0.55}>
        {/* upper wings */}
        <path d="M60 56 C40 22 18 18 14 34 C10 50 30 56 58 60 Z" fill={`url(#bw${id})`} />
        <path d="M60 56 C80 22 102 18 106 34 C110 50 90 56 62 60 Z" fill={`url(#bw${id})`} />
        {/* lower wings */}
        <path d="M60 60 C44 78 26 92 30 104 C36 112 54 92 60 70 Z" fill={`url(#bw${id})`} />
        <path d="M60 60 C76 78 94 92 90 104 C84 112 66 92 60 70 Z" fill={`url(#bw${id})`} />
        {/* dark veining + border */}
        <path d="M60 56 C40 22 18 18 14 34 C10 50 30 56 58 60" fill="none" stroke="#3a2c22" strokeWidth="2.5" />
        <path d="M60 56 C80 22 102 18 106 34 C110 50 90 56 62 60" fill="none" stroke="#3a2c22" strokeWidth="2.5" />
        <path d="M60 60 C44 78 26 92 30 104 C36 112 54 92 60 70" fill="none" stroke="#3a2c22" strokeWidth="2.5" />
        <path d="M60 60 C76 78 94 92 90 104 C84 112 66 92 60 70" fill="none" stroke="#3a2c22" strokeWidth="2.5" />
        {/* spots */}
        {[[26,32],[34,40],[92,32],[84,40],[38,92],[80,92]].map(([x,y],i)=>(
          <circle key={i} cx={x} cy={y} r="2.6" fill="#fff5e6" opacity="0.92" />
        ))}
        {/* body + antennae */}
        <ellipse cx="60" cy="64" rx="3.4" ry="20" fill="#2b2622" />
        <path d="M58 46 C54 38 50 34 47 33" stroke="#2b2622" strokeWidth="1.6" fill="none" />
        <path d="M62 46 C66 38 70 34 73 33" stroke="#2b2622" strokeWidth="1.6" fill="none" />
      </Paint>
    </svg>
  );
}

/* ------------------------------- Wildflower ------------------------------ */
export function Wildflower({ size = 70, color = '#e8b84b', style, className = '' }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" className={className} style={style} aria-hidden="true">
      <Paint idBase={`wf${id}`} blur={0.45}>
        <path d="M40 44 C40 60 39 70 38 78" stroke="#5e6f44" strokeWidth="2.4" fill="none" strokeLinecap="round" />
        <path d="M40 58 C30 54 24 56 20 62 C30 64 36 62 40 58 Z" fill="#7a8b5a" />
        {[0,1,2,3,4,5,6,7].map((i)=>(
          <ellipse key={i} cx="40" cy="22" rx="5.5" ry="12" fill={color}
            transform={`rotate(${i*45} 40 34)`} opacity="0.92" />
        ))}
        <circle cx="40" cy="34" r="7" fill="#caa42f" />
        <circle cx="40" cy="34" r="7" fill="none" stroke="#a8841f" strokeWidth="1" opacity="0.5" />
      </Paint>
    </svg>
  );
}

/* Tiny inline glyphs for inline use in headings/dividers */
export function Sprig({ size = 22, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" style={style} aria-hidden="true">
      <path d="M20 38 C19 26 19 14 20 4" stroke="#7a8b5a" strokeWidth="2.2" fill="none" strokeLinecap="round" />
      <path d="M20 12 C12 9 8 11 6 16 C13 17 18 15 20 12 Z" fill="#8ba067" />
      <path d="M20 22 C28 19 32 21 34 26 C27 27 22 25 20 22 Z" fill="#7a8b5a" />
    </svg>
  );
}
